import { parseStack } from "../utils/stackFrames";

/**
 * Records DocumentStore.dispatch calls for the DevTools Dispatch tab.
 *
 * Off by default; while off, dispatch pays one boolean check. Enable from the
 * DevTools tab, `localStorage.siDispatchMonitoring = "true"`, or
 * `window.__DISPATCH_MONITORING__.setEnabled(true)`. Unlike Reflux monitoring
 * it needs no reload, since every dispatch goes through this module.
 *
 * @module document/dispatchMonitor
 */

/** localStorage key for the enabled flag. */
export const STORAGE_KEY = "siDispatchMonitoring";
/** Entries kept; older ones are dropped first. */
export const MAX_ENTRIES = 200;
/** Payloads larger than this (JSON characters) are summarized instead of kept. */
export const MAX_PAYLOAD_CHARS = 100_000;
/** Listener notifications are coalesced to at most one per interval. */
const NOTIFY_INTERVAL_MS = 100;

/**
 * @typedef {Object} PayloadSnapshot
 * @prop {string} [json] - serialized payload; absent when there was none
 * @prop {number} [size] - JSON length, set when the payload was too large to keep
 * @prop {string} [error] - why the payload couldn't be serialized
 */

/**
 * @typedef {Object} DispatchEntry
 * @prop {number} id
 * @prop {string} type - command type
 * @prop {number} time - Date.now() at dispatch
 * @prop {number} durationMs - from dispatch until the first round of the action's
 *   handlers finished. Actions those handlers fire in turn run later and aren't counted.
 * @prop {PayloadSnapshot} payload - taken at dispatch, before handlers can mutate it
 * @prop {string|null} error - message if dispatch threw
 * @prop {{stack?: string}} [callSite] - holder whose `.stack` is formatted on first read
 * @prop {string|null} [source] - set by getEntrySource on first read
 */

const readStoredEnabled = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch (e) {
    return false;
  }
};

let enabled = readStoredEnabled();
let nextId = 1;
/** @type {DispatchEntry[]} */
const entries = [];
/** @type {Map<string, number>} */
const counts = new Map();
const listeners = new Set();
/** @type {ReturnType<typeof setTimeout>|null} */
let notifyTimer = null;
/** Bumped on every change; the cached snapshot is rebuilt only when it moves. */
let version = 0;
let snapshot = null;

const changed = () => {
  version += 1;
  if (notifyTimer != null) return;
  notifyTimer = setTimeout(() => {
    notifyTimer = null;
    listeners.forEach((listener) => {
      try {
        listener();
      } catch (e) {
        // A broken panel must not affect dispatch.
      }
    });
  }, NOTIFY_INTERVAL_MS);
};

/**
 * Serializes a payload now, so later mutation of the caller's object doesn't
 * change the log. Immutable values serialize through their `toJSON`, VO
 * instances as their fields. Parsing is left to whoever opens the entry.
 * @param {*} payload
 * @returns {PayloadSnapshot}
 */
const snapshotPayload = (payload) => {
  try {
    const json = JSON.stringify(payload);
    if (json === undefined) return {};
    if (json.length > MAX_PAYLOAD_CHARS) return { size: json.length };
    return { json };
  } catch (e) {
    return { error: e.message ?? String(e) };
  }
};

/** @param {unknown} error @returns {string|null} */
const errorMessage = (error) => (error ? (error.message ?? String(error)) : null);

/**
 * @param {Omit<DispatchEntry, "id">} fields
 */
const addEntry = (fields) => {
  const entry = { id: nextId++, ...fields };
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.shift();
  counts.set(entry.type, (counts.get(entry.type) ?? 0) + 1);
  changed();
};

/** @returns {boolean} */
export const isDispatchMonitoringEnabled = () => enabled;

/**
 * Turns recording on or off and remembers the choice for this browser.
 * @param {boolean} value
 */
export const setDispatchMonitoringEnabled = (value) => {
  enabled = Boolean(value);
  try {
    if (enabled) localStorage.setItem(STORAGE_KEY, "true");
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Storage unavailable: the setting just won't survive a reload.
  }
  changed();
};

/**
 * Runs one dispatch and records it. The entry is added in a microtask queued
 * after `route` returns: Reflux actions run their listeners in a microtask
 * queued during `route`, so by then the first round of handlers (and their
 * synchronous store cascade) has finished.
 * @param {{type: string, payload?: any}} command
 * @param {(command: object) => any} route - the real dispatch
 * @param {Function} dispatchFn - omitted from the captured stack, with everything above it
 * @returns {*} whatever `route` returns
 */
export const monitorDispatch = (command, route, dispatchFn) => {
  const callSite = {};
  if (typeof Error.captureStackTrace === "function") Error.captureStackTrace(callSite, dispatchFn);
  else callSite.stack = new Error().stack;

  const type = String(command?.type);
  const payload = snapshotPayload(command?.payload);
  const time = Date.now();
  const start = performance.now();
  const record = (error) =>
    addEntry({
      type,
      payload,
      time,
      durationMs: performance.now() - start,
      error: errorMessage(error),
      callSite,
    });

  let result;
  try {
    result = route(command);
  } catch (error) {
    record(error);
    throw error;
  }
  queueMicrotask(() => record());
  return result;
};

/**
 * Adds an entry directly, without running a dispatch. For tests and the console.
 * @param {{type: string, payload?: any, time?: number, durationMs?: number, error?: unknown, callSite?: {stack?: string}}} record
 */
export const recordDispatch = ({ type, payload, time, durationMs = 0, error, callSite }) =>
  addEntry({
    type: String(type),
    payload: snapshotPayload(payload),
    time: time ?? Date.now(),
    durationMs,
    error: errorMessage(error),
    callSite,
  });

/**
 * "File.jsx:12" for the entry's first app frame, parsed on first read and kept.
 * @param {DispatchEntry} entry
 * @returns {string|null}
 */
export const getEntrySource = (entry) => {
  if (entry.source === undefined) {
    const frame = parseStack(entry.callSite?.stack).find((f) => !f.library);
    entry.source = frame?.shortLocation ?? null;
  }
  return entry.source;
};

/**
 * @typedef {Object} DispatchSnapshot
 * @prop {boolean} enabled
 * @prop {DispatchEntry[]} log - newest first
 * @prop {Array<{type: string, count: number}>} counts - highest first, since the last clear
 * @prop {number} total - dispatches since the last clear (not limited to MAX_ENTRIES)
 */

/**
 * Current state, rebuilt only after a change: repeated reads return the same
 * object until something changes, as useSyncExternalStore expects.
 * @returns {DispatchSnapshot}
 */
export const getDispatchSnapshot = () => {
  if (snapshot?.version !== version) {
    const sortedCounts = [...counts.entries()]
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
    snapshot = {
      version,
      enabled,
      log: entries.slice().reverse(),
      counts: sortedCounts,
      total: sortedCounts.reduce((sum, { count }) => sum + count, 0),
    };
  }
  return snapshot;
};

/** Empties the log and the per-command totals. */
export const clearDispatchLog = () => {
  entries.length = 0;
  counts.clear();
  changed();
};

/**
 * Calls `listener` (coalesced) whenever the log or the enabled state changes.
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribeToDispatchLog = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Test-only: reset module state. */
export const _resetForTests = () => {
  enabled = false;
  nextId = 1;
  entries.length = 0;
  counts.clear();
  listeners.clear();
  if (notifyTimer != null) clearTimeout(notifyTimer);
  notifyTimer = null;
  version += 1;
};

if (typeof window !== "undefined") {
  window.__DISPATCH_MONITORING__ = {
    setEnabled: setDispatchMonitoringEnabled,
    isEnabled: isDispatchMonitoringEnabled,
    getSnapshot: getDispatchSnapshot,
    clear: clearDispatchLog,
  };
}
